import { z } from 'zod'
import { oc } from '@orpc/contract'

// Mirrors INCOMING_FAX_STATUSES in src/model/incoming-fax/types.ts. Inlined
// here because this contract module is bundled into the web client via
// @behuman/core-contract; transitive imports from the model layer would drag
// in tsconfig path aliases (@/...) that the web Vite build can't resolve.
export const INCOMING_FAX_STATUSES = ['new', 'viewed', 'triaged'] as const

// Mirrors `listIncomingFaxes` args (sans MedplumClient, which the handler
// pulls off the request context). Filter is the same recursive
// FilterExpression tree the model accepts, so callers can express
// and/or/not over status, receivedAt, and id without the handler having
// to re-translate flat query params.

const orderDirectionSchema = z.enum(['asc', 'desc'])

const orderEntrySchema = z.union([
  z.object({ id: orderDirectionSchema }),
  z.object({ receivedAt: orderDirectionSchema }),
])

const filterIdSchema = z.object({ exact: z.string() })

const filterReceivedAtSchema = z.union([
  z.object({ exact: z.string() }),
  z.object({ before: z.string() }),
  z.object({ after: z.string() }),
])

const filterStatusSchema = z.object({
  in: z.array(z.enum(INCOMING_FAX_STATUSES)),
})

const filterLeafSchema = z.object({
  type: z.literal('filter'),
  id: filterIdSchema.optional(),
  receivedAt: filterReceivedAtSchema.optional(),
  status: filterStatusSchema.optional(),
})

type FilterExpressionInput =
  | z.infer<typeof filterLeafSchema>
  | { type: 'all' }
  | { type: 'none' }
  | { type: 'expression'; operator: 'and' | 'or' | 'not'; operands: FilterExpressionInput[] }

const filterExpressionSchema: z.ZodType<FilterExpressionInput> = z.lazy(() =>
  z.union([
    filterLeafSchema,
    z.object({ type: z.literal('all') }),
    z.object({ type: z.literal('none') }),
    z.object({
      type: z.literal('expression'),
      operator: z.enum(['and', 'or', 'not']),
      operands: z.array(filterExpressionSchema),
    }),
  ]),
)

const pageRequestSchema = z.object({
  offset: z.number().int().min(0),
  count: z.number().int().min(0),
  limit: z.number().int().min(0),
})

export const incomingFaxWithSchema = z.object({
  title: z.boolean().optional(),
  patient: z.boolean().optional(),
  provider: z.boolean().optional(),
  attachment: z.boolean().optional(),
})

const humanNameSchema = z.object({
  use: z.enum(['usual', 'official', 'temp', 'nickname', 'anonymous', 'old', 'maiden']).optional(),
  text: z.string().optional(),
  family: z.string().optional(),
  given: z.array(z.string()).optional(),
  prefix: z.array(z.string()).optional(),
  suffix: z.array(z.string()).optional(),
})

const patientSchema = z.object({
  id: z.string(),
  active: z.boolean(),
  name: z.array(humanNameSchema),
})

const providerSchema = z.object({
  id: z.string(),
  active: z.boolean(),
  name: z.array(humanNameSchema),
})

const faxAttachmentSchema = z.object({
  contentType: z.string().optional(),
  url: z.string().optional(),
  data: z.string().optional(),
  title: z.string().optional(),
  size: z.number().optional(),
})

// IncomingFax._documentReference is `Reference<DocumentReference>` — in
// practice always emitted as the cheap `{ id }` shape, so the wire schema
// matches that form rather than the broader union. The underscore prefix
// signals the field is a discouraged transitional pointer.
const documentReferenceSchema = z.object({ id: z.string() })

export const incomingFaxSchema = z.object({
  id: z.string(),
  receivedAt: z.string(),
  senderPhone: z.string(),
  pageCount: z.number(),
  status: z.enum(INCOMING_FAX_STATUSES),
  title: z.string().nullable(),
  patient: patientSchema.optional(),
  provider: providerSchema.optional(),
  attachment: faxAttachmentSchema.optional(),
  _documentReference: documentReferenceSchema.optional(),
})

export const listIncomingFaxes = oc
  .route({ method: 'POST', path: '/v2/incoming-faxes' })
  .input(
    z.object({
      page: pageRequestSchema,
      filter: filterExpressionSchema.optional(),
      order: z.array(orderEntrySchema).optional(),
      with: incomingFaxWithSchema.optional(),
    }),
  )
  .output(
    z.object({
      page: pageRequestSchema.extend({
        size: z.number().int().min(0),
        total: z.number().int().min(0),
      }),
      data: z.array(incomingFaxSchema),
    }),
  )
